import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import type { CalendarSong } from '../types';
import { NO_SONG, toggleValue } from './calendarFilterModel';
import { songArtworkUrl, songColourStyle } from './calendarPostView';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongFilterMenuProps {
  /** The songs with a post or a release day in the range, upcoming first. */
  songs: CalendarSong[];
  /** The post count of each song key (`NO_SONG` for the posts with no song). */
  counts: Map<string, number>;
  selected: string[];
  onChange: (songs: string[]) => void;
}

/** The Song menu (design b8): "All songs", each song with its cover, and "No song". */
const SongFilterMenuComponent: React.FC<SongFilterMenuProps> = ({ songs, counts, selected, onChange }) => {
  const intl = useIntl();
  const [isOpen, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((current) => !current), []);
  const first = songs.find((song) => song.catalogItemUuid === selected[0]);
  const label =
    selected.length === 0
      ? intl.formatMessage({ id: 'app.sml.promote.calendar.filter.allSongs', defaultMessage: 'All songs' })
      : selected.length === 1 && first !== undefined
        ? first.title
        : intl.formatMessage(
            { id: 'app.sml.promote.calendar.filter.songCount', defaultMessage: '{count} songs' },
            { count: selected.length }
          );
  const countText = (key: string) =>
    intl.formatMessage(
      {
        id: 'app.sml.promote.calendar.filter.postCount',
        defaultMessage: '{count, plural, one {# post} other {# posts}}',
      },
      { count: counts.get(key) ?? 0 }
    );

  return (
    <Dropdown isOpen={isOpen} toggle={toggle}>
      <DropdownToggle tag="button" type="button" className={clsx(styles.fbtn, selected.length > 0 && styles.fbtnOn)}>
        {label}
        <span className={styles.caret} aria-hidden="true" />
      </DropdownToggle>
      <DropdownMenu className={styles.menu}>
        <DropdownItem toggle={false} className={styles.menuItem} onClick={() => onChange([])}>
          <input type="checkbox" readOnly checked={selected.length === 0} tabIndex={-1} />
          <b>{intl.formatMessage({ id: 'app.sml.promote.calendar.filter.allSongs', defaultMessage: 'All songs' })}</b>
        </DropdownItem>
        {songs.map((song) => {
          const cover = songArtworkUrl(song);
          return (
            <DropdownItem
              key={song.catalogItemUuid}
              toggle={false}
              className={styles.menuItem}
              onClick={() => onChange(toggleValue(selected, song.catalogItemUuid))}
            >
              <input type="checkbox" readOnly checked={selected.includes(song.catalogItemUuid)} tabIndex={-1} />
              {cover === null ? (
                <span className={styles.menuCover} style={songColourStyle(song)} />
              ) : (
                <img className={styles.menuCover} src={cover} alt="" />
              )}
              <span className={styles.popText}>
                <b>{song.title}</b>
                <small>{countText(song.catalogItemUuid)}</small>
              </span>
            </DropdownItem>
          );
        })}
        <DropdownItem
          toggle={false}
          className={styles.menuItem}
          onClick={() => onChange(toggleValue(selected, NO_SONG))}
        >
          <input type="checkbox" readOnly checked={selected.includes(NO_SONG)} tabIndex={-1} />
          <span className={styles.popText}>
            <b>{intl.formatMessage({ id: 'app.sml.promote.calendar.filter.noSong', defaultMessage: 'No song' })}</b>
            <small>{countText(NO_SONG)}</small>
          </span>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const SongFilterMenu = TypedMemo(SongFilterMenuComponent);
